import React, { useMemo, useState, useRef } from "react";

const FROM = [250, 232, 244];
const TO = [176, 0, 110];
const DAYS = ["Pondelok", "Utorok", "Streda", "Štvrtok", "Piatok", "Sobota", "Nedeľa"];
const DAYS_SHORT = ["Po", "Ut", "St", "Št", "Pi", "So", "Ne"];
const DEFAULT_START = 8;
const DEFAULT_END = 20;

function lerp(a, b, t) {
    return Math.round(a + (b - a) * t);
}

function normalizeMatrix(matrix) {
    const source = matrix?.length === 7 ? matrix : Array.from({ length: 7 }, () => []);
    return source.map((row) => {
        const next = [...(row || [])];
        while (next.length < 24) next.push(0);
        return next.slice(0, 24);
    });
}

function HourDayHeatmap({ matrix = [] }) {
    const wrapperRef = useRef(null);
    const [tooltip, setTooltip] = useState(null);
    const [pointer, setPointer] = useState({ x: 0, y: 0 });

    const normalized = useMemo(() => normalizeMatrix(matrix), [matrix]);

    const { max, peak, startH, endH, dayTotals } = useMemo(() => {
        let m = 1;
        let pk = { d: -1, h: -1, v: -1 };
        const totals = normalized.map((row) => row.reduce((a, b) => a + (b || 0), 0));
        const colHas = Array(24).fill(false);
        normalized.forEach((row, d) =>
            row.forEach((v, h) => {
                if (v > m) m = v;
                if (v > 0) colHas[h] = true;
                if (v > pk.v) pk = { d, h, v };
            })
        );
        const dataS = colHas.indexOf(true);
        const dataE = colHas.lastIndexOf(true);
        let s = DEFAULT_START;
        let e = DEFAULT_END;
        if (dataS !== -1) {
            s = Math.min(s, dataS);
            e = Math.max(e, dataE);
        }
        return { max: m, peak: pk, startH: s, endH: e, dayTotals: totals };
    }, [normalized]);

    const colorFor = (value) => {
        if (!value) return "#f0f1f4";
        const t = Math.pow(Math.min(1, value / max), 0.6);
        return `rgb(${lerp(FROM[0], TO[0], t)}, ${lerp(FROM[1], TO[1], t)}, ${lerp(FROM[2], TO[2], t)})`;
    };

    const textColor = (value) => {
        if (!value) return "transparent";
        return Math.pow(Math.min(1, value / max), 0.6) > 0.5 ? "#fff" : "#7a3060";
    };

    const handleMove = (event) => {
        const rect = wrapperRef.current?.getBoundingClientRect();
        if (!rect) return;
        setPointer({ x: event.clientX - rect.left, y: event.clientY - rect.top });
    };

    const hours = [];
    for (let h = startH; h <= endH; h++) hours.push(h);
    const maxDayTotal = Math.max(1, ...dayTotals);

    return (
        <div ref={wrapperRef} onMouseMove={handleMove} style={{ position: "relative", width: "100%", overflowX: "auto" }}>
            {tooltip && (
                <div style={{
                    position: "absolute",
                    left: pointer.x + 14,
                    top: pointer.y + 14,
                    zIndex: 5,
                    pointerEvents: "none",
                    background: "rgba(33,37,41,0.95)",
                    color: "#fff",
                    padding: "8px 12px",
                    borderRadius: 8,
                    fontSize: "0.82rem",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
                    whiteSpace: "nowrap",
                }}>
                    <div style={{ fontWeight: "bold", marginBottom: 2 }}>{tooltip.day}, {tooltip.hour}:00–{tooltip.hour + 1}:00</div>
                    <div style={{ opacity: 0.85 }}>
                        Vyhľadávania: <strong>{tooltip.value > 0 ? tooltip.value : "—"}</strong>
                    </div>
                </div>
            )}

            <div style={{ minWidth: 40 + hours.length * 34 + 56 }}>
                <div style={{ display: "flex", alignItems: "flex-end", marginBottom: 6 }}>
                    <div style={{ width: 40, flexShrink: 0 }} />
                    {hours.map((h) => (
                        <div key={h} style={{ flex: 1, textAlign: "center", fontSize: "0.68rem", color: "#999", fontWeight: 600 }}>
                            {h}
                        </div>
                    ))}
                    <div style={{ width: 56, flexShrink: 0, textAlign: "right", fontSize: "0.66rem", color: "#bbb", textTransform: "uppercase" }}>Spolu</div>
                </div>

                {normalized.map((row, d) => (
                    <div key={d} style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
                        <div style={{ width: 40, flexShrink: 0, fontSize: "0.78rem", color: "#777", fontWeight: "bold" }}>{DAYS_SHORT[d]}</div>
                        {hours.map((h) => {
                            const value = row[h] || 0;
                            const isPeak = peak.d === d && peak.h === h && value > 0;
                            return (
                                <div
                                    key={h}
                                    onMouseEnter={() => setTooltip({ day: DAYS[d], hour: h, value })}
                                    onMouseLeave={() => setTooltip(null)}
                                    style={{
                                        flex: 1,
                                        height: 30,
                                        margin: 1.5,
                                        borderRadius: 4,
                                        background: colorFor(value),
                                        color: textColor(value),
                                        fontSize: "0.7rem",
                                        fontWeight: "bold",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        boxShadow: isPeak ? "0 0 0 2px #0b1f2a" : "none",
                                    }}
                                >
                                    {value || ""}
                                </div>
                            );
                        })}
                        <div style={{ width: 56, flexShrink: 0, textAlign: "right", paddingLeft: 6 }}>
                            <span style={{ fontWeight: "bold", color: dayTotals[d] > 0 ? "#444" : "#c5c9d0", fontSize: "0.82rem" }}>{dayTotals[d]}</span>
                            <div style={{ height: 4, marginTop: 2, borderRadius: 2, background: "#f1f1f3" }}>
                                <div style={{ width: `${(dayTotals[d] / maxDayTotal) * 100}%`, height: "100%", borderRadius: 2, background: dayTotals[d] > 0 ? "rgb(176, 0, 110)" : "#e5e7eb" }} />
                            </div>
                        </div>
                    </div>
                ))}

                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, paddingLeft: 40, color: "#999", fontSize: "0.78rem" }}>
                    <span>0</span>
                    <div style={{ flex: 1, height: 8, borderRadius: 4, background: `linear-gradient(90deg, ${colorFor(0)}, ${colorFor(max)})` }} />
                    <span>{peak.v > 0 ? `max ${max} vyhľadávaní / hodina` : "čaká sa na dáta"}</span>
                </div>
            </div>
        </div>
    );
}

export default HourDayHeatmap;
